import { create } from "zustand";

import type { CoherenceText, TacticalCoherence } from "../../api/types";
import { lang, tServer } from "../../i18n/i18n";

/** Cohérence tactique (docs/24) : action en attente du panneau, ouvert d'office avant la validation d'un ordre ou
 *  le téléchargement du dossier complet, et demande d'ouverture d'une rubrique de l'ordre.
 *
 *  - `gate` « approve » : « Valider l'OPO » (ou l'OPLAN) a été demandé alors que des points restent ouverts ; le
 *    panneau propose de valider (désigné quand plus rien ne bloque) ou de passer outre avec une justification.
 *    `confirm` rend la main à la vue de l'ordre, qui valide (`confirmed`, lu une seule fois).
 *  - `gate` « download » : téléchargement du dossier complet demandé ; jamais bloqué, le panneau avertit et
 *    « Télécharger quand même » lance le téléchargement (`run`).
 *  - `fieldTarget` : paragraphe d'un ordre désigné par un constat, ouvert dans l'éditeur par la vue de l'ordre. */

export type CoherenceGate =
  | { kind: "approve"; number: number; oplan: boolean; frago?: boolean }
  | { kind: "download"; run: () => void };

interface CoherenceState {
  gate: CoherenceGate | null;
  confirmed: { number: number; override?: string; nonce: number } | null;
  fieldTarget: { number: number; field: string; nonce: number } | null;
  open: (gate: CoherenceGate) => void;
  clear: () => void;
  confirm: (number: number, override?: string) => void;
  takeConfirmed: (number: number) => { override?: string } | null;
  openField: (number: number, field: string) => void;
  takeField: (number: number) => string | null;
}

export const useCoherence = create<CoherenceState>()((set, get) => ({
  gate: null,
  confirmed: null,
  fieldTarget: null,
  open: (gate) => set({ gate }),
  clear: () => set({ gate: null }),
  confirm: (number, override) => set({ gate: null, confirmed: { number, override, nonce: Date.now() } }),
  takeConfirmed: (number) => {
    const c = get().confirmed;
    if (!c || c.number !== number) return null;
    set({ confirmed: null });
    return { override: c.override };
  },
  openField: (number, field) => set({ fieldTarget: { number, field, nonce: Date.now() } }),
  takeField: (number) => {
    const target = get().fieldTarget;
    if (!target || target.number !== number) return null;
    set({ fieldTarget: null });
    return target.field;
  },
}));

/** Texte d'un constat dans la langue de l'interface. Le contrôle de l'OPO repris tel quel n'écrit qu'en français :
 *  sa traduction passe par les messages du serveur (`tServer`). */
export function coherenceText(text: CoherenceText): string {
  if (lang() !== "en") return text.fr;
  return text.en && text.en !== text.fr ? text.en : tServer(text.fr);
}

/** Points ouverts qui méritent l'arrêt avant une validation ou un téléchargement : bloquants et à revoir. */
export function openPoints(report: TacticalCoherence | undefined): number {
  return report ? report.counts.blocking + report.counts.review : 0;
}

/** Déclencheurs (validation d'un ordre, téléchargement du dossier complet) : le panneau s'ouvre d'abord quand le
 *  rapport lu juste avant compte des points ouverts ; un rapport illisible ou un dossier incomplet ne retient
 *  rien (la validation garde le contrôle du serveur, le téléchargement n'est jamais bloqué). */
export function gateNeeded(report: TacticalCoherence | null | undefined): boolean {
  return Boolean(report && !report.incomplete && openPoints(report) > 0);
}

/** Clé du cache du rapport : relu dès que le dossier change de version (carte, Données initiales). */
export function coherenceKey(exerciseId: string | null, opo: number, version?: number | null) {
  return ["coherence", exerciseId, opo, version ?? null] as const;
}
